import type { LoginView, TokenPair } from '@/types/api'
import { SKIP_REFRESH_HEADER, get, post } from '@/utils/request'

/**
 * 认证端点（附录B §4.1）。
 *
 * 登录类请求一律带 SKIP_REFRESH：此刻还没有令牌可言，若让 401 触发刷新
 * 就会把「密码错了」变成「先去刷新一次再说」，既浪费一轮往返也掩盖真实错误。
 */
/** 登录方式（附录C §3.1）：后端 AuthMethod 枚举在登录页的子集，微信登录走独立入口不在此列。 */
export type LoginMethod = 'PASSWORD' | 'SMS' | 'EMAIL'

const ALL_LOGIN_METHODS: readonly LoginMethod[] = ['PASSWORD', 'SMS', 'EMAIL']

function parseLoginMethods(raw: string | undefined): LoginMethod[] {
  const parsed = [...new Set((raw ?? '').split(',').map((item) => item.trim().toUpperCase()))].filter(
    (item): item is LoginMethod => ALL_LOGIN_METHODS.includes(item as LoginMethod),
  )
  // 密码登录必须常驻：通道（短信网关 / 邮件服务）没配好的时候，
  // 它是唯一还能让用户走进系统的路，把它过滤掉等于把所有人锁在登录页外面
  return parsed.includes('PASSWORD') ? parsed : ['PASSWORD', ...parsed]
}

/**
 * 登录页实际开放的登录方式（附录C §3.1）。
 *
 * 短信 / 邮箱验证码依赖外部通道（短信服务商、邮件服务）与配额，通道没接好时
 * 露出入口只是让用户多点一次再收到一句"发送失败"，所以默认只开放密码登录。
 * 通道就绪后配 `VITE_LOGIN_METHODS=PASSWORD,SMS,EMAIL` 即可打开，无需改代码；
 * 非法取值一律忽略，不会把登录页渲染成没有可用方式的空壳。
 */
export const loginMethods: LoginMethod[] = parseLoginMethods(import.meta.env.VITE_LOGIN_METHODS)

export interface PasswordLoginPayload {
  /** 用户名 / 手机号 / 邮箱：由服务端自行判定（附录D §2.1） */
  identifier: string
  password: string
  channel?: string
  /**
   * 要落进去的应用：由服务端按 client_id 反查，前端不传应用主键 ——
   * 否则调用方可以"用 A 的 client_id 换令牌、把应用指到 B"，也白送一条枚举扫描的入口。
   */
  clientId?: string | null
}

/** 登录页要展示的应用信息（附录C §3.1）。 */
export interface ClientInfo {
  /** 就是 client_id：登录页把它原样回传，服务端据此反查要落进哪个应用 */
  clientId: string
  name: string
  iconUrl: string | null
  mfaRequired: boolean
  authMethods: string[]
}

export interface CodeLoginPayload {
  target: string
  code: string
  channel?: string
  clientId?: string | null
}

export const authApi = {
  /** 发送验证码（登录 / 绑定 / 注销二次确认共用）。 */
  sendCode: (channel: string, target: string, scene = 'LOGIN') =>
    post<void>('/auth/v1/codes', { channel, target, scene }, { headers: { [SKIP_REFRESH_HEADER]: '1' } }),

  passwordLogin: (payload: PasswordLoginPayload) =>
    post<LoginView>('/auth/v1/login/password', payload, { headers: { [SKIP_REFRESH_HEADER]: '1' } }),

  codeLogin: (payload: CodeLoginPayload) =>
    post<LoginView>('/auth/v1/login/code', payload, { headers: { [SKIP_REFRESH_HEADER]: '1' } }),

  /** 补验 MFA（TOTP 动态码 / 恢复码）。 */
  mfaLogin: (pendingToken: string, code: string) =>
    post<LoginView>('/auth/v1/login/mfa', { pendingToken, code }, { headers: { [SKIP_REFRESH_HEADER]: '1' } }),

  /** 强制改密后完成登录。 */
  resetPassword: (pendingToken: string, newPassword: string) =>
    post<LoginView>('/auth/v1/login/password:reset', { pendingToken, newPassword }, {
      headers: { [SKIP_REFRESH_HEADER]: '1' },
    }),

  /**
   * 会话端点（附录C §2 / 附录F §13.2）：
   * 两个端点都依赖 Cookie `uc_rt` + 头 `X-UC-Client: web`，请求体为空。
   */
  refresh: () => post<TokenPair>('/auth/v1/token/refresh', null, { headers: { [SKIP_REFRESH_HEADER]: '1' } }),
  logout: () => post<void>('/auth/v1/logout', null, { headers: { [SKIP_REFRESH_HEADER]: '1' } }),

  /**
   * 切换当前会话所在的应用：按目标应用重签令牌。
   *
   * 同样带 SKIP_REFRESH：这里本来就是在换令牌，若 401 再触发一次刷新，
   * 用户会看到"切了个应用结果被踢回登录页"的怪现象。
   */
  switchApp: (clientId: string) =>
    post<TokenPair>('/auth/v1/switch-app', { clientId }, {
      headers: { [SKIP_REFRESH_HEADER]: '1' },
    }),

  /** 登录页按 client_id 反查应用：拿到展示信息后，同一个 client_id 回传即为"要落进哪个应用"。 */
  client: (clientId: string) =>
    get<ClientInfo>(`/auth/v1/clients/${encodeURIComponent(clientId)}`),
}
